import { useEffect, useState } from "react";
import { LocateFixed, Pause, Play } from "lucide-react";

type TimerCardProps = {
  activeSince: Date | null;
  onStart: () => Promise<unknown>;
  onStop: () => Promise<unknown>;
  busy: boolean;
};

/**
 * Quello che l'operaio ha appena chiesto, prima che il giro completo sia finito.
 *
 * L'avvio vero non e' immediato: la posizione richiede fino a otto secondi sul campo,
 * e finche' non arriva non si puo' scrivere niente. Restare fermi su "Pronto a iniziare"
 * per tutto quel tempo fa premere il pulsante due volte. Qui si mostra subito lo stato
 * chiesto, si lascia decidere il server e si torna indietro se il server dice di no.
 */
type Richiesta = { tipo: "avviato"; da: Date } | { tipo: "fermato" } | null;

function elapsedLabel(activeSince: Date | null, now: number) {
  if (!activeSince) return "00:00:00";
  const seconds = Math.max(0, Math.floor((now - activeSince.getTime()) / 1000));
  const hours = Math.floor(seconds / 3600).toString().padStart(2, "0");
  const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
  const remainder = (seconds % 60).toString().padStart(2, "0");
  return `${hours}:${minutes}:${remainder}`;
}

export function TimerCard({ activeSince, onStart, onStop, busy }: TimerCardProps) {
  const [now, setNow] = useState(Date.now());
  const [richiesta, setRichiesta] = useState<Richiesta>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // Riconciliazione: appena il server conferma quello che era stato solo mostrato,
  // la richiesta locale sparisce e comanda di nuovo il dato vero.
  useEffect(() => {
    setRichiesta((precedente) => {
      if (!precedente) return null;
      if (precedente.tipo === "avviato" && activeSince) return null;
      if (precedente.tipo === "fermato" && !activeSince) return null;
      return precedente;
    });
  }, [activeSince]);

  const active = richiesta ? richiesta.tipo === "avviato" : Boolean(activeSince);
  const mostraDa = richiesta?.tipo === "avviato" ? richiesta.da : active ? activeSince : null;

  useEffect(() => {
    if (!mostraDa) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [mostraDa]);

  async function esegui(azione: "avvia" | "ferma") {
    const precedente = richiesta;
    setErrore(null);
    setRichiesta(azione === "avvia" ? { tipo: "avviato", da: new Date() } : { tipo: "fermato" });
    setInCorso(true);
    try {
      await (azione === "avvia" ? onStart() : onStop());
    } catch (error) {
      // Il server ha rifiutato: la scheda torna com'era e lo dice, senza lasciare
      // in giro un lavoro che nessuno ha registrato.
      setRichiesta(precedente);
      setErrore(error instanceof Error ? error.message : "Operazione non riuscita.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <section className={`timer-card${active ? " timer-card--active" : ""}`}>
      <div>
        <p className="timer-card__state">{active ? "Lavoro in corso" : "Pronto a iniziare"}</p>
        <strong className="timer-card__time">{elapsedLabel(mostraDa, now)}</strong>
        <p className="timer-card__hint"><LocateFixed size={16} /> La posizione viene rilevata solo quando inizi o termini.</p>
        {(inCorso || busy) && <p className="timer-card__saving" aria-live="polite">Registrazione in corso, puoi continuare a lavorare.</p>}
        {errore && <p className="timer-card__error" role="alert">{errore}</p>}
      </div>
      <button
        className={`timer-action${active ? " timer-action--stop" : ""}`}
        type="button"
        onClick={() => void esegui(active ? "ferma" : "avvia")}
        disabled={inCorso || busy}
      >
        {active ? <Pause size={24} /> : <Play size={24} fill="currentColor" />}
        {active ? "Termina lavoro" : "Inizia lavoro"}
      </button>
    </section>
  );
}
